import React from 'react';
import PublicHeader from './PublicHeader';

interface PrivacyPolicyPageProps {
  onNavigateHome: () => void;
}

const PrivacyPolicyPage: React.FC<PrivacyPolicyPageProps> = ({ onNavigateHome }) => {
  return (
    <div className="min-h-screen bg-gray-50">
      <PublicHeader title="Privacy Policy" />
      {/* Header */}
      <div className="bg-fishing-blue text-white py-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-3xl font-bold">Privacy Policy</h1>
          <p className="mt-2 text-blue-200">Tumbulgum Fishing Club Inc.</p>
          <p className="text-blue-200 text-sm mt-1">Last updated: August 2026</p>
        </div>
      </div>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="bg-white shadow rounded-lg p-8 space-y-8 text-gray-700 leading-relaxed">

          {/* Introduction */}
          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-3">1. Introduction</h2>
            <p>
              Tumbulgum Fishing Club Inc. (<strong>"the Club"</strong>, <strong>"we"</strong>, <strong>"us"</strong>) is committed to protecting the privacy of our members and website visitors. This Privacy Policy explains how we collect, use, store, and disclose personal information in accordance with the <em>Privacy Act 1988</em> (Cth) and the Australian Privacy Principles (APPs).
            </p>
            <p className="mt-3">
              This policy applies to members, competition participants, and visitors to our website at <strong>tumbyfc.com</strong>, and explains how we handle your personal information.
            </p>
          </section>

          {/* What we collect */}
          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-3">2. What Personal Information We Collect</h2>
            <p>We collect the following personal information from members and competition participants:</p>
            <ul className="list-disc ml-6 mt-3 space-y-1">
              <li>Full name</li>
              <li>Email address</li>
              <li>Phone number</li>
              <li>Residential address (street, suburb, state, postcode)</li>
              <li>Date of birth</li>
              <li>Gender</li>
              <li>Emergency contact name and phone number</li>
              <li>Guardian details (for junior members, 16 or under on 1 January)</li>
              <li>Shirt size (for merchandise purposes)</li>
              <li>Membership type and payment information</li>
              <li>Competition participation records and catch submissions</li>
              <li>Catch photos (including GPS location data embedded in photo metadata)</li>
              <li>Profile photo (if uploaded)</li>
            </ul>
            <p className="mt-3">
              We collect this information directly from you when you register for membership, participate in competitions, or use our member portal.
            </p>
            <p className="mt-3">
              <strong>Card details are never collected or stored by the Club.</strong> When you pay
              online, your card information is entered directly into a secure payment form hosted by
              our payment provider, Square, and is never received by or stored on our systems. We
              retain only a record that a payment was made, the amount, and a payment reference.
            </p>
          </section>

          {/* Why we collect it */}
          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-3">3. Why We Collect Personal Information</h2>
            <p>We collect and use your personal information to:</p>
            <ul className="list-disc ml-6 mt-3 space-y-1">
              <li>Process and manage your club membership</li>
              <li>Communicate with you about club events, competitions, and renewals</li>
              <li>Administer fishing competitions and maintain competition records</li>
              <li>Verify catch submissions and maintain competition integrity</li>
              <li>Comply with our obligations under the <em>Fisheries Management Act 1994</em> (NSW) and relevant fishing regulations</li>
              <li>Contact your emergency contact in the event of an incident</li>
              <li>Process membership fees and competition entry payments</li>
              <li>Maintain club records as required by our constitution</li>
            </ul>
          </section>

          {/* GPS and photo data */}
          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-3">4. GPS Location Data and Catch Photos</h2>
            <p>
              When you submit a catch photo during a competition, our system may extract GPS coordinates embedded in the photo's metadata (EXIF data). This location data is used solely to verify that catches were made within the designated competition zone. GPS data is stored alongside your catch submission record and is only accessible to club administrators.
            </p>
            <p className="mt-3">
              Catch photos are stored securely in cloud storage located in Australia. Photos may be reviewed by club administrators as part of the competition adjudication process.
            </p>
            <p className="mt-3">
              By submitting a photo to the Club — including catch photos, Happy Snaps, and event photos — you agree it may be used by the Club for Club purposes, including on the Club's website, social media, and other online channels. See the Waiver and Release for the full terms.
            </p>
          </section>

          {/* AI verification */}
          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-3">5. Automated Processing (AI Verification)</h2>
            <p>
              Catch photos submitted during competitions are processed by an automated
              image-analysis system that assists administrators in verifying competition entries.
              This system checks for:
            </p>
            <ul className="list-disc ml-6 mt-3 space-y-1">
              <li>Whether a fish is visible on a measuring mat</li>
              <li>Whether the fish species appears consistent with the submitted species</li>
              <li>Whether the competition event sticker is visible</li>
              <li>Whether the fish length appears consistent with the submitted measurement</li>
              <li>Whether the photo appears to be a genuine photograph</li>
            </ul>
            <p className="mt-3">
              This automated analysis is <strong>advisory only</strong>. No submission is automatically rejected based on AI analysis. All final decisions are made by human administrators. The AI system does not retain or learn from your photos beyond the immediate analysis.
            </p>
          </section>

          {/* Storage and security */}
          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-3">6. How We Store and Protect Your Information</h2>
            <p>
              Your personal information is stored securely using cloud infrastructure located in
              Australia. We implement the following security measures:
            </p>
            <ul className="list-disc ml-6 mt-3 space-y-1">
              <li>Encrypted data storage and transmission</li>
              <li>Access controls limiting data access to authorised administrators only</li>
              <li>Secure sign-in using industry-standard authentication</li>
              <li>Multi-factor authentication required for administrator accounts</li>
              <li>Regular security reviews and updates</li>
            </ul>
            <p className="mt-3">
              We retain your personal information for as long as your membership is active and for a reasonable period thereafter as required by our club constitution and applicable law. If you request deletion of your account, we will remove your personal information subject to any legal retention obligations.
            </p>
          </section>

          {/* Disclosure */}
          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-3">7. Disclosure of Personal Information</h2>
            <p>We do not sell, rent, or trade your personal information. We may disclose your information to:</p>
            <ul className="list-disc ml-6 mt-3 space-y-1">
              <li><strong>Club committee members and administrators</strong> — for the purposes of managing your membership and competition participation</li>
              <li><strong>Amazon Web Services (AWS)</strong> — our cloud hosting provider, which stores the Club's data in Australia and processes catch photos for the automated checks described in section 5, subject to AWS's own privacy and security commitments</li>
              <li><strong>Square</strong> — our payment provider, which handles card payments directly (see section 2)</li>
              <li><strong>Law enforcement or regulatory bodies</strong> — where required by law or to protect the safety of members</li>
            </ul>
            <p className="mt-3">
              Competition results (competitor names and catch measurements) may be published publicly on our website when results are finalised. If you do not wish your name to appear in published results, please contact us before the competition.
            </p>
          </section>

          {/* Your rights */}
          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-3">8. Your Rights</h2>
            <p>Under the Australian Privacy Principles, you have the right to:</p>
            <ul className="list-disc ml-6 mt-3 space-y-1">
              <li><strong>Access</strong> the personal information we hold about you</li>
              <li><strong>Correct</strong> inaccurate or out-of-date information</li>
            </ul>
            <p className="mt-3">
              In addition, although not required by the Australian Privacy Principles, the Club
              will honour a request to <strong>delete</strong> your personal information where we
              are not required to keep it, and you may <strong>opt out</strong> of non-essential
              communications at any time.
            </p>
            <p className="mt-3">
              To exercise any of these rights, please contact us at <a href="mailto:fishing@tumbyfc.com" className="text-fishing-blue hover:underline">fishing@tumbyfc.com</a>.
            </p>
          </section>

          {/* Cookies */}
          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-3">9. Cookies and Website Analytics</h2>
            <p>
              Our website uses session cookies for sign-in purposes only.
            </p>
            <p className="mt-3">
              We use a website monitoring service to understand how the site is performing and to detect technical errors. It records the pages visited, browser type and version, operating system, device type, an approximate location worked out from your internet address (country, state and city — not a precise location), how quickly pages loaded, and any technical errors encountered.
            </p>
            <p className="mt-3">
              This information is not linked to your membership record and does not identify you. It does not place cookies on your device, and it does not track you between visits or across other websites. It is stored in Australia and automatically deleted after 30 days.
            </p>
            <p className="mt-3">
              We do not use advertising or marketing analytics services such as Google Analytics, and we do not share information with advertising networks.
            </p>
          </section>

          {/* Complaints */}
          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-3">10. Complaints</h2>
            <p>
              If you believe we have breached your privacy, please contact us in the first instance at <a href="mailto:fishing@tumbyfc.com" className="text-fishing-blue hover:underline">fishing@tumbyfc.com</a>. We will respond within 30 days.
            </p>
            <p className="mt-3">
              If you are not satisfied with our response, you may lodge a complaint with the Office of the Australian Information Commissioner (OAIC) at <a href="https://www.oaic.gov.au" target="_blank" rel="noopener noreferrer" className="text-fishing-blue hover:underline">www.oaic.gov.au</a> or by calling 1300 363 992.
            </p>
          </section>

          {/* Changes */}
          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-3">11. Changes to This Policy</h2>
            <p>
              We may update this Privacy Policy from time to time. The current version will always be available on our website. We will notify members of material changes via email.
            </p>
          </section>

          {/* Contact */}
          <section className="border-t pt-6">
            <h2 className="text-xl font-bold text-gray-900 mb-3">Contact Us</h2>
            <p>
              <strong>Tumbulgum Fishing Club Inc.</strong><br />
              Tumbulgum, NSW 2490<br />
              Email: <a href="mailto:fishing@tumbyfc.com" className="text-fishing-blue hover:underline">fishing@tumbyfc.com</a><br />
              Website: <a href="https://tumbyfc.com" className="text-fishing-blue hover:underline">tumbyfc.com</a>
            </p>
          </section>

        </div>
      </main>

      {/* Footer */}
      <footer className="bg-gray-800 text-gray-400 py-6 mt-10">
        <div className="max-w-4xl mx-auto px-4 text-center text-sm">
          <p>&copy; 2026 Tumbulgum Fishing Club. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default PrivacyPolicyPage;
